import { ChangeEvent, useEffect, useState } from 'react';

import {
  DevHubMenuItem,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import ApplicationGroupSearch from '../../common/ApplicationGroupSearch';
import {
  ApplicationGroup,
  FunctionalArea,
  ServiceDomain,
} from '../../insights/types/InsightsTypes';
import FunctionalAreaSelect from '../../common/FunctionalAreaSelect';
import { InformationClassificationPolicy } from '../../insights/types/PolicyTypes';
import { ClassificationSelect } from '../../common/ClassificationSelect';
import { useAppBulkEditContext } from './context/AppBulkEditContext';
import { ValidationType } from './AppBulkEdit';
import { gql, useQuery } from '@apollo/client';
import { PartySearch } from '../../common/PartySearch';
import { RoleSearch } from '../../insights/roles/RoleSearch';
import { SelectFieldOptions } from './types/appBulkEditTypes';
import {
  AppGroup,
  AddFunctionalAreaConnectionProperty,
  InformationClassificationPolicyProperty,
  RoleProperty,
  ServiceDomainProperty,
} from './types';
import {
  ResourceIdentityType,
  ResponsibleParty,
} from '../../insights/types/RoleTypes';
import { OwnerProperty } from './types/updateApplication/OwnerProperty';

type AppBulkEditAddNewValueProps = {
  setValidationHasError: (value: ValidationType) => void;
};

const GetDomainsQuery = gql`
  query PortfolioItems {
    resources {
      portfolioItems {
        id
        name
      }
    }
  }
`;

/**
 * Component for selecting fields to edit in bulk edit.
 * @param props - The component props.
 * @param props.setValidationHasError - Function to set validation error state.
 */
const AppBulkEditAddNewValue = ({
  setValidationHasError,
}: AppBulkEditAddNewValueProps): JSX.Element => {
  const { data: domainData } = useQuery(GetDomainsQuery, {
    errorPolicy: 'all',
  });

  const {
    property,
    field,
    setSelectedParty,
    setSelectedRole,
    selectedRole,
    selectedParty,
  } = useAppBulkEditContext();

  const [appGroup, setAppGroup] = useState<ApplicationGroup>(
    {} as ApplicationGroup,
  );
  const [functionalArea, setFunctionalArea] = useState<FunctionalArea>(
    {} as FunctionalArea,
  );
  const [infoClass, setInfoClass] = useState<InformationClassificationPolicy>(
    {} as InformationClassificationPolicy,
  );

  const [domain, setDomain] = useState<ServiceDomain>({} as ServiceDomain);
  const [serviceDomains, setServiceDomains] = useState([]);

  const [selectedOwner, setSelectedOwner] = useState<ResponsibleParty>(
    {} as ResponsibleParty,
  );

  useEffect(() => {
    if (appGroup && property instanceof AppGroup) {
      property.set(appGroup);
      return;
    }
    if (
      functionalArea &&
      property instanceof AddFunctionalAreaConnectionProperty
    ) {
      property.set(functionalArea);
      return;
    }
    if (
      infoClass &&
      property instanceof InformationClassificationPolicyProperty
    ) {
      property.set(infoClass);
      return;
    }
    if (domain && property instanceof ServiceDomainProperty) {
      property.set(domain);
      return;
    }
    if (selectedRole && selectedParty && property instanceof RoleProperty) {
      property.set({
        role: selectedRole,
        party: selectedParty,
        identityType:
          selectedParty?.__typename === 'User'
            ? ResourceIdentityType.User
            : ResourceIdentityType.Group,
      });
    }
    if (selectedOwner && property instanceof OwnerProperty) {
      property.set(selectedOwner);
    }
  }, [
    appGroup,
    functionalArea,
    infoClass,
    domain,
    selectedRole,
    selectedParty,
    selectedOwner,
    property,
  ]);

  const handleDomainChange = (event: ChangeEvent<{ value: unknown }>): void => {
    const selectedDomain = serviceDomains.find(
      (value: ServiceDomain) => value.id === event.target.value,
    );
    if (selectedDomain) {
      setDomain(selectedDomain);
    }
  };

  useEffect(() => {
    const mutationInput = property.propertyMutationInput();
    const hasUndefinedValue = Object.values(mutationInput).some(
      value => value === undefined || value === '',
    );

    setValidationHasError({
      hasError: hasUndefinedValue,
      errorMessage: hasUndefinedValue ? 'Select a value to proceed' : '',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    property,
    setValidationHasError,
    selectedParty,
    selectedRole,
    domain,
    selectedOwner,
    infoClass,
    functionalArea,
    appGroup,
  ]);

  useEffect(() => {
    setServiceDomains(domainData?.resources?.portfolioItems);
  }, [domainData]);

  const fieldComponents = {
    [SelectFieldOptions.APP_GROUP]: (
      <ApplicationGroupSearch
        selectedAppGroup={appGroup}
        setAppGroup={setAppGroup}
        helperText="A grouping of one or more Components that share the same release timelines, dependencies and resources. An Application has many Components."
      />
    ),
    [SelectFieldOptions.FUNCTIONAL_AREA]: (
      <FunctionalAreaSelect
        noMargin={false}
        selectedFunctionalArea={functionalArea}
        setFunctionalArea={setFunctionalArea}
      />
    ),
    [SelectFieldOptions.INFORMATION_CLASSIFICATION_POLICY]: (
      <ClassificationSelect
        name="icp"
        size="small"
        setSelectedParty={setInfoClass}
        selectedParty={infoClass}
        required={false}
      />
    ),
    [SelectFieldOptions.OWNER]: (
      <PartySearch
        helperText="Assign new owner. Compliance requirements prevent contractors from being assigned as Application Owner."
        setSelectedParty={setSelectedOwner}
        contractors={false}
        groupEnabled={false}
        selectedParty={selectedOwner}
      />
    ),
    [SelectFieldOptions.SERVICE_DOMAIN]: (
      <DevHubTextField
        name="serviceDomain"
        size="small"
        select
        variant="outlined"
        label="Select new domain"
        value={domain?.id}
        onChange={handleDomainChange}
      >
        {serviceDomains?.map((option: ServiceDomain) => {
          return (
            <DevHubMenuItem
              // onChange={() => handleDomainChange(option)}
              key={option?.id}
              value={option?.id}
            >
              {option?.name}
            </DevHubMenuItem>
          );
        })}
      </DevHubTextField>
    ),
    [SelectFieldOptions.ROLES]: (
      <>
        <RoleSearch
          setSelectedRole={setSelectedRole}
          selectedRole={selectedRole}
        />
        <PartySearch
          helperText="Party you wish to add to the role"
          groupEnabled
          setSelectedParty={setSelectedParty}
          selectedParty={selectedParty}
        />
      </>
    ),
  };

  return (
    fieldComponents[field] || (
      <DevHubTypography>Select a valid field</DevHubTypography>
    )
  );
};

export default AppBulkEditAddNewValue;
